import { readClickCookies } from '@/utils/marketingParams'

/**
 * Payload for the `recordTouchEvent` GraphQL mutation. Keys are camelCase to
 * match the backend `MarketingTouchEventInput` argument names.
 */
export interface TouchEventInput {
  eventId: string
  anonymousId: string
  landingUrl: string
  source: string
  referrerUrl?: string
  userAgent?: string
  clickIds?: Record<string, string>
  cookies?: Record<string, string>
  utm?: Record<string, string>
}

/** This frontend's capture origin, stamped on every touch row. */
export const TOUCH_SOURCE = 'kettle'

// URL query param -> GraphQL clickIds field (camelCase).
export const CLICK_ID_PARAMS: Record<string, string> = {
  fbclid: 'fbclid',
  gclid: 'gclid',
  gbraid: 'gbraid',
  wbraid: 'wbraid',
  msclkid: 'msclkid',
  gad_source: 'gadSource',
  gclsrc: 'gclsrc',
  ttclid: 'ttclid',
}

const UTM_KEYS = ['source', 'medium', 'campaign', 'content', 'term'] as const

/**
 * Builds the touch payload from the current landing: a fresh event_id, the
 * RudderStack anonymousId, the full landing URL, and the click ids (URL),
 * persistent cookies, and utm params that are present. Client-only (reads
 * window/document/cookies).
 */
export const buildTouchEventInput = ({ anonymousId }: { anonymousId: string }): TouchEventInput => {
  const params = new URLSearchParams(window.location.search)

  const clickIds: Record<string, string> = {}
  for (const [param, field] of Object.entries(CLICK_ID_PARAMS)) {
    const value = params.get(param)
    if (value) clickIds[field] = value
  }

  const cookieSnapshot = readClickCookies()
  const cookies: Record<string, string> = {}
  if (cookieSnapshot.fbc) cookies.fbc = cookieSnapshot.fbc
  if (cookieSnapshot.fbp) cookies.fbp = cookieSnapshot.fbp
  if (cookieSnapshot.gcl_aw) cookies.gclAw = cookieSnapshot.gcl_aw

  const utm: Record<string, string> = {}
  for (const key of UTM_KEYS) {
    const value = params.get(`utm_${key}`)
    if (value) utm[key] = value
  }

  const input: TouchEventInput = {
    eventId: crypto.randomUUID(),
    anonymousId,
    landingUrl: window.location.href,
    source: TOUCH_SOURCE,
  }

  if (document.referrer) input.referrerUrl = document.referrer
  if (navigator.userAgent) input.userAgent = navigator.userAgent
  if (Object.keys(clickIds).length) input.clickIds = clickIds
  if (Object.keys(cookies).length) input.cookies = cookies
  if (Object.keys(utm).length) input.utm = utm

  return input
}
